import type { TFunction } from "i18next";
import { findBlock, findRow } from "../domain/find";
import {
  translateBlockLabel,
  translateButtonRowSlotLabel,
  translateColumnNumberLabel,
  translateGroupLabel,
} from "../domain/labels";
import type { EmailDocument, Selection } from "../domain/types";

export type SettingsSurfaceMeta = {
  title: string;
  description: string;
};

export function resolveSettingsSurfaceMeta(
  selection: Selection,
  document: EmailDocument,
  t: TFunction,
): SettingsSurfaceMeta {
  switch (selection.kind) {
    case "email":
      return {
        title: t("Settings"),
        description: t("Manage email layout and appearance defaults."),
      };
    case "group":
      return {
        title: translateGroupLabel(t),
        description: t(
          "Manage this row group’s padding, corner rounding, and appearance.",
        ),
      };
    case "row":
      return findRow(document, selection.rowId)
        ? {
            title: t("Row settings"),
            description: t(
              "Manage this row’s layout, padding, corner rounding and appearance.",
            ),
          }
        : {
            title: t("Row settings"),
            description: t("Manage row layout and appearance."),
          };
    case "column": {
      const column = translateColumnNumberLabel(t, document, selection.rowId, selection.columnId);
      return {
        title: t("{{column}} settings", { column }),
        description: t(
          "Manage this column’s alignment, padding, rounding and appearance.",
        ),
      };
    }
    case "buttonRowSlot": {
      const block = findBlock(document, selection.rowId, selection.columnId, selection.blockId);
      if (block?.type !== "buttonRow") {
        return {
          title: t("Button settings"),
          description: t("Manage button content, layout, and appearance."),
        };
      }
      return {
        title: translateButtonRowSlotLabel(t, selection.slotIndex),
        description: t("Manage this button’s content, layout, and appearance."),
      };
    }
    case "block": {
      const block = findBlock(document, selection.rowId, selection.columnId, selection.blockId);
      const label = block ? translateBlockLabel(t, block) : t("Block");
      return {
        title: t("{{block}} settings", { block: label }),
        description: t(
          "Manage this {{block}} block’s content, layout, and appearance.",
          { block: label.toLowerCase() },
        ),
      };
    }
  }
}
